import React, { useState } from 'react'
import styled from 'styled-components'
import * as Sentry from '@sentry/browser'
import { useTranslation } from 'react-i18next'
import { animateScroll } from 'react-scroll'
import { Button, Modal } from 'components/base-ui'
import Fonts, { weight } from 'components/base-ui/Fonts'
import ModalWithImage, { useModalWithImage } from 'components/modals/ModalWithImage'
import { DineInCoupon } from 'components/PromotionCouponCard'
import PromotionDetails from 'components/PromotionDetails'
import {
  AdjustmentInvalidReasonEnumType,
  AdjustmentTargetEntityTypeEnum,
  OrderPromotionInvalidEnum,
  useApplyPromoToOrderMutation,
  useRemoveOrderAdjustmentMutation,
} from 'graphQL/dinein/generate/operations'
import { sendAnalyticsDineInSelectPromotion } from 'helpers/analytics'
import { validateAdjusments } from 'helpers/promotion'
import { isDevelopment } from 'variables/environment'
import { dineInClient } from 'variables/services'
import {
  CartPromotionsType,
  HandleApplyPromoCode,
  HandleRemoveCartAdjustment,
  HandleToShowPromotionDetails,
  OrderType,
} from '.'
import PromotionList from './PromotionList'

interface PromotionContainerProps {
  orderID: number
  allowedSharingCoupon: boolean
  onClickRefreshCoupon: VoidFunction
  onClickCombineCoupon: VoidFunction
  isLoadingPage: boolean
  orderAndMemberCoupons: OrderType | undefined
  memberCouponsFP: DineInCoupon[]
  memberCouponsHH: DineInCoupon[]
  memberPromotions: CartPromotionsType
  generalPromotions: CartPromotionsType
  onRefetch: () => Promise<any>
  setIsCashVoucherErrorZerodue: (value: boolean) => void
  onClickBack: VoidFunction
}

const PromotionContainer = ({
  orderID,
  allowedSharingCoupon,
  onClickRefreshCoupon,
  onClickCombineCoupon,
  isLoadingPage,
  orderAndMemberCoupons,
  memberCouponsFP,
  memberCouponsHH,
  memberPromotions,
  generalPromotions,
  onRefetch,
  setIsCashVoucherErrorZerodue,
  onClickBack,
}: PromotionContainerProps) => {
  const { t } = useTranslation()
  const [applyPromoToOrder] = useApplyPromoToOrderMutation({ client: dineInClient })
  const [removeOrderAdjustment] = useRemoveOrderAdjustmentMutation({
    client: dineInClient,
  })

  const [isProcessing, setIsProcessing] = useState(false)
  const [promotionIDApplying, setPromotionIDApplying] = useState(0)
  const [serialNumberApplying, setSerialNumberApplying] = useState('')
  const [removeAdjustmentProcessing, setRemoveAdjustmentProcessing] = useState(false)
  const [isShowModalNoticeCustomAlreadyHavePromotionInCart, setIsShowModalNoticeCustomAlreadyHavePromotionInCart] =
    useState<boolean>(false)
  const [currentPromotionDetails, setCurrentPromotionDetails] = useState<DineInCoupon | null>(null)
  const [currentTargetEntity, setCurrentTargetEntity] = useState<
    keyof typeof AdjustmentTargetEntityTypeEnum | undefined
  >(undefined)
  const [currentHaveMemberCondition, setCurrentHaveMemberCondition] = useState<boolean>(false)
  const [currentIsAutoPromotion, setCurrentIsAutoPromotion] = useState<boolean>(false)
  const [currentInvalidReason, setCurrentInvalidReason] = useState<AdjustmentInvalidReasonEnumType | undefined>(
    undefined
  )
  const [isShowPromotionDetails, setIsShowPromotionDetails] = useState<boolean>(false)
  const [isOpenModalInvalidCoupon, setIsOpenModalInvalidCoupon] = useState<boolean>(false)

  const {
    props: modalQRCodeErrorProps,
    show: modalQRCodeErrorShow,
    close: modalQRCodeErrorClose,
  } = useModalWithImage({
    imagePath: `${process.env.PUBLIC_URL}/assets/promotions/scan-qrcode-error.png`,
    imageSize: '120px',
    isGenerateSrc: true,
  })

  const handleApplyPromoCode: HandleApplyPromoCode = async function ({
    id = 0,
    promoCode = '',
    externalMemberCouponRefID,
    hatoHeartMemberCouponID,
    onCompleted,
    onFailed,
  }) {
    if (isProcessing) {
      return
    }

    try {
      setPromotionIDApplying(id)
      setSerialNumberApplying(promoCode)
      setIsProcessing(true)
      await applyPromoToOrder({
        variables: {
          orderID,
          promoCode: promoCode,
          externalMemberCouponRefID: externalMemberCouponRefID,
          hatoHeartMemberCouponID: hatoHeartMemberCouponID,
        },
        onCompleted: ({ applyPromoToOrder }) => {
          const promotionInvalids = applyPromoToOrder?.promotionInvalids ?? []

          if (promotionInvalids.length === 0) {
            const order = applyPromoToOrder.order
            if (order) {
              const validate = validateAdjusments(order.adjustments)
              setIsCashVoucherErrorZerodue(!validate)
            }
            let promotionID
            if (promoCode) {
              promotionID = promoCode
            } else if (externalMemberCouponRefID) {
              promotionID = externalMemberCouponRefID
            } else {
              promotionID = hatoHeartMemberCouponID
            }
            sendAnalyticsDineInSelectPromotion({ promotionID })
            onCompleted && onCompleted()
            animateScroll.scrollToTop({
              containerId: 'scroll-apply-promotion',
              duration: 600,
            })
            setTimeout(async () => {
              await onRefetch().finally(() => setIsProcessing(false))
            })
          } else {
            setIsProcessing(false)

            if (promotionInvalids.includes(OrderPromotionInvalidEnum.Conflicting)) {
              setIsShowModalNoticeCustomAlreadyHavePromotionInCart(true)

              // This feature is auto removeAdjustment and add new promotion to cart. ( Now: Disabled )
              // setIsShowModalConfirmToRemoveCouponBeforeApplyNewCoupon(true)
              // setPromoCodeToConfirmApplyAfterRemove(promoCode)
              // setExternalCouponIDToConfirmApplyAfterRemove(externalMemberCouponRefID)
              // setMemberCouponIDToConfirmApplyAfterRemove(hatoHeartMemberCouponID)
            } else if (promotionInvalids.includes(OrderPromotionInvalidEnum.QrCodeNotFound)) {
              modalQRCodeErrorShow({
                title: t('modal.modalApplyPromoErrorStaffQRCodeNotFound.title'),
                description: t('modal.modalApplyPromoErrorStaffQRCodeNotFound.des'),
                buttonText: t('btnOK'),
                onRequestClose: () => {
                  modalQRCodeErrorClose()
                },
              })
            } else if (promotionInvalids.includes(OrderPromotionInvalidEnum.InvalidLineId)) {
              modalQRCodeErrorShow({
                title: t('modal.modalApplyPromoErrorStaffInvalidLineID.title'),
                description: t('modal.modalApplyPromoErrorStaffInvalidLineID.des'),
                buttonText: t('btnOK'),
                onRequestClose: () => {
                  modalQRCodeErrorClose()
                },
              })
            } else if (promotionInvalids.includes(OrderPromotionInvalidEnum.StaffPrivilegeAlreadyApplied)) {
              modalQRCodeErrorShow({
                title: t('modal.modalApplyPromoErrorStaffPrivilegeAlreadyApplied.title'),
                buttonText: t('btnOK'),
                onRequestClose: () => {
                  modalQRCodeErrorClose()
                },
              })
            } else {
              setIsOpenModalInvalidCoupon(true)
            }
            onFailed && onFailed(promotionInvalids)
          }
        },
      })
    } catch (err) {
      onFailed && onFailed()
      !isDevelopment &&
        Sentry.captureException(new Error(`HatoHub error: Apply promocode to cart ${JSON.stringify(err)}`))
    } finally {
      setIsShowPromotionDetails(false)
      setPromotionIDApplying(0)
      setSerialNumberApplying('')
    }
  }

  const handleToShowPromotionDetails: HandleToShowPromotionDetails = (
    promotion,
    targetEntity,
    haveMemberCondition,
    isAutoPromotion,
    invalidReason
  ) => {
    setCurrentPromotionDetails(promotion)
    setCurrentTargetEntity(targetEntity)
    setCurrentHaveMemberCondition(haveMemberCondition)
    setCurrentIsAutoPromotion(isAutoPromotion)
    setCurrentInvalidReason(invalidReason)
    setIsShowPromotionDetails(true)
  }

  const handleRemoveCartAdjustment: HandleRemoveCartAdjustment = async function ({
    externalCouponID,
    promotionID,
    hatoHeartMemberCouponID,
    onCompleted,
    onFailed,
  }) {
    if (!orderAndMemberCoupons?.id || isProcessing || removeAdjustmentProcessing) {
      return false
    }
    setRemoveAdjustmentProcessing(true)
    removeOrderAdjustment({
      variables: {
        orderID: orderID,
        promotionID: promotionID,
        externalCouponID: externalCouponID,
        hatoHeartMemberCouponID,
      },
      onCompleted: async ({ removeOrderAdjustment }) => {
        const promotionInvalids = removeOrderAdjustment.promotionInvalids ?? []
        if (promotionInvalids.length === 0) {
          onCompleted && onCompleted()
        } else {
          setIsOpenModalInvalidCoupon(true)
          onFailed && onFailed(promotionInvalids)
        }
        await onRefetch()
        setTimeout(() => setRemoveAdjustmentProcessing(false), 500)
      },
      onError(error) {
        setTimeout(() => setRemoveAdjustmentProcessing(false), 500)
        onFailed && onFailed()
        !isDevelopment &&
          Sentry.captureException(new Error(`HatoHub error: Apply promocode to cart ${JSON.stringify(error)}`))
      },
    })
  }

  const cartAdjustmentsForDetail =
    orderAndMemberCoupons?.adjustments.map((item) => ({
      ...item,
      memberCouponID: item.externalCoupon ? parseInt(item.externalCoupon.memberCouponRefID) : 0,
    })) || []

  return (
    <>
      <PromotionList
        onApplyPromoCode={handleApplyPromoCode}
        onRemoveCartAdjustment={handleRemoveCartAdjustment}
        isLoadingPage={isLoadingPage}
        isProcessing={isProcessing}
        promotionIDApplying={promotionIDApplying}
        serialNumberApplying={serialNumberApplying}
        orderAndMemberCoupons={orderAndMemberCoupons}
        memberCouponsFP={memberCouponsFP}
        memberCouponsHH={memberCouponsHH}
        memberPromotions={memberPromotions}
        generalPromotions={generalPromotions}
        onClickCombineCoupon={onClickCombineCoupon}
        onClickRefreshCoupon={onClickRefreshCoupon}
        onClickBack={onClickBack}
        allowedSharingCoupon={allowedSharingCoupon}
        handleToShowPromotionDetails={handleToShowPromotionDetails}
      />

      <ModalWithImage {...modalQRCodeErrorProps} />

      <Modal
        isOpen={isOpenModalInvalidCoupon}
        onRequestClose={() => setIsOpenModalInvalidCoupon(false)}
        zindex="9999"
        hideCloseButton={false}
      >
        <ModalContainer>
          <ModalTitleGeneric>{t('modalWarningCannotAddCouponTitle')}</ModalTitleGeneric>
          <ModalText>{t('modalWarningCannotAddCouponDes')}</ModalText>
        </ModalContainer>
      </Modal>

      <Modal
        isOpen={isShowModalNoticeCustomAlreadyHavePromotionInCart}
        onRequestClose={() => setIsShowModalNoticeCustomAlreadyHavePromotionInCart(false)}
        hideCloseButton={false}
      >
        <ModalContainer>
          <ModalTitle>{t('ModalNoticeCustomAlreadyHavePromotionInCartTitle')}</ModalTitle>
          <ModalText style={{ marginBottom: '20px' }}>{t('ModalNoticeCustomAlreadyHavePromotionInCartDes')}</ModalText>
          <Button
            onClick={() => {
              setIsShowModalNoticeCustomAlreadyHavePromotionInCart(false)
              setIsShowPromotionDetails(false)
            }}
            md
            minWidth="175px"
            enableFastClick={false}
          >
            {t('ModalNoticeCustomAlreadyHavePromotionInCartBtn')}
          </Button>
        </ModalContainer>
      </Modal>

      <OverlayPage show={isShowPromotionDetails}>
        <PromotionDetails
          cartAdjustments={cartAdjustmentsForDetail}
          data={currentPromotionDetails}
          onBack={() => setIsShowPromotionDetails(false)}
          handleApplyPromoCode={handleApplyPromoCode}
          handleRemoveCartAdjustment={handleRemoveCartAdjustment}
          haveMemberCondition={currentHaveMemberCondition}
          targetEntity={currentTargetEntity}
          isAutoPromotion={currentIsAutoPromotion}
          invalidReason={currentInvalidReason}
        />
      </OverlayPage>
    </>
  )
}

export default PromotionContainer

const ModalContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const ModalTitle = styled.div`
  font-weight: ${weight.medium};
  font-size: 20px;
  color: ${(props) => props.theme.colors.gray900};
  text-align: center;
  margin-top: 20px;
  white-space: pre-wrap;
  line-height: 1.4em;
`

const ModalText = styled.div`
  font-weight: ${weight.normal};
  font-size: 14px;
  color: ${(props) => props.theme.colors.gray700};
  text-align: center;
  margin-top: 10px;
  margin-bottom: 10px;
  white-space: pre-wrap;
`

const ModalTitleGeneric = styled(Fonts.H2)`
  margin-top: 15px;
  margin-bottom: 5px;
  text-align: center;
  color: black;
`

const OverlayPage = styled.div<{ show: boolean; fromLeft?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100;
  background-color: white;
  transform: translate3d(100%, 0, 0);
  transition: 0.3s;
  pointer-events: none;
  visibility: hidden;

  ${(props) =>
    props.fromLeft
      ? `
    transform: translate3d(-100%, 0, 0);
    `
      : ''}

  ${(props) =>
    props.show
      ? `
    visibility: visible;
    pointer-events: auto;
    transform: translate3d(0, 0, 0);
    `
      : ''}
`
